import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useData } from '../../../context/DataContext';
import { apiFunctions } from '../../../apiService/apiFunctions';
import { colors } from '../../../constants/colors';
import { commonStyles } from '../../../constants/commonStyles';
import BookCard from '../../../components/BookCard';
import BottomNavigation from '../../../components/BottomNavigation';

const FILE_BASE_URL = 'https://api.kitabcloud.se/storage/';

const ProfileScreen = () => {
    const { token, logout } = useAuth();
    const { likedBooks: cachedLikedBooks, fetchLikedBooks: fetchCachedLikedBooks } = useData();
    const navigate = useNavigate();
    const [userData, setUserData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('profile'); // 'profile' or 'liked'

    // Extract liked books from cached data
    const likedBooks = useMemo(() => {
        if (!cachedLikedBooks || !Array.isArray(cachedLikedBooks) || cachedLikedBooks.length === 0) {
            return [];
        }
        
        // Response structure: cachedLikedBooks[0].booklike is an array of { id, book: {...} }
        const booklikeArray = cachedLikedBooks[0]?.booklike || [];
        return booklikeArray.map(item => item.book).filter(Boolean);
    }, [cachedLikedBooks]);

    useEffect(() => {
        if (token) {
            fetchUserData();
            // Only fetch if we don't have cached data
            if (!cachedLikedBooks) {
                fetchCachedLikedBooks(token);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const fetchUserData = async () => {
        try {
            const data = await apiFunctions.getUserData(token);
            setUserData(data);
        } catch (error) {
            console.error('Error fetching user data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = async () => {
        try {
            await logout();
            navigate('/login');
        } catch (error) {
            console.error('Logout error:', error);
        }
    };

    // Always show bottom navigation - load content separately
    return (
        <>
        <div style={{
            ...commonStyles.fullScreenContainer,
            paddingBottom: '80px' // Account for bottom navigation
        }}>
            <div style={{
                ...commonStyles.fullScreenInnerContainer,
                paddingBottom: '20px'
            }}>
                {/* Tab Navigation */}
                <div style={{
                    display: 'flex',
                    marginBottom: 20,
                    borderBottom: `1px solid ${colors.lightGrey}`
                }}>
                    <button
                        onClick={() => setActiveTab('profile')}
                        style={{
                            flex: 1,
                            padding: '12px',
                            background: 'none',
                            border: 'none',
                            borderBottom: activeTab === 'profile' ? `2px solid ${colors.appPrimary}` : 'none',
                            color: activeTab === 'profile' ? colors.appPrimary : colors.grey,
                            fontWeight: activeTab === 'profile' ? '600' : '400',
                            cursor: 'pointer'
                        }}
                    >
                        Profile
                    </button>
                    <button
                        onClick={() => setActiveTab('liked')}
                        style={{
                            flex: 1,
                            padding: '12px',
                            background: 'none',
                            border: 'none',
                            borderBottom: activeTab === 'liked' ? `2px solid ${colors.appPrimary}` : 'none',
                            color: activeTab === 'liked' ? colors.appPrimary : colors.grey,
                            fontWeight: activeTab === 'liked' ? '600' : '400',
                            cursor: 'pointer'
                        }}
                    >
                        Liked Items ({likedBooks.length})
                    </button>
                </div>

                {activeTab === 'profile' ? (
                    /* Profile Content */
                    <div>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 16,
                            marginBottom: 24,
                            padding: 20,
                            backgroundColor: colors.lightestGrey,
                            borderRadius: 12
                        }}>
                            <div style={{
                                width: 60,
                                height: 60,
                                borderRadius: '50%',
                                backgroundColor: colors.appPrimary,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: colors.white,
                                fontSize: 24,
                                fontWeight: 'bold'
                            }}>
                                {userData?.full_name?.charAt(0)?.toUpperCase() || 'U'}
                            </div>
                            <div>
                                <h2 style={commonStyles.textLightBold(20, { 
                                    color: colors.black,
                                    marginBottom: 5
                                })}>
                                    {userData?.full_name || 'User'}
                                </h2>
                                <p style={commonStyles.textLightNormal(14, { 
                                    color: colors.grey
                                })}>
                                    {userData?.email || 'user@example.com'}
                                </p>
                            </div>
                        </div>

                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(2, 1fr)',
                            gap: 15,
                            marginBottom: 24
                        }}>
                            <div style={{
                                textAlign: 'center',
                                padding: '15px',
                                backgroundColor: colors.lightestGrey,
                                borderRadius: 8
                            }}>
                                <h3 style={commonStyles.textLightBold(18, { 
                                    color: colors.black,
                                    marginBottom: 5
                                })}>
                                    {likedBooks.length}
                                </h3>
                                <p style={commonStyles.textLightNormal(12, { 
                                    color: colors.grey
                                })}>
                                    Liked Books
                                </p>
                            </div>
                            <div style={{
                                textAlign: 'center',
                                padding: '15px',
                                backgroundColor: colors.lightestGrey,
                                borderRadius: 8
                            }}>
                                <h3 style={commonStyles.textLightBold(18, { 
                                    color: colors.black,
                                    marginBottom: 5
                                })}>
                                    {userData?.bookmark_books?.length || 0}
                                </h3>
                                <p style={commonStyles.textLightNormal(12, { 
                                    color: colors.grey
                                })}>
                                    Bookmarked
                                </p>
                            </div>
                        </div>

                        <button
                            onClick={handleLogout}
                            style={{
                                width: '100%',
                                padding: '15px',
                                backgroundColor: colors.red,
                                color: colors.white,
                                border: 'none',
                                borderRadius: 8,
                                fontSize: 16,
                                fontWeight: '600',
                                cursor: 'pointer'
                            }}
                        >
                            Logout
                        </button>
                    </div>
                ) : (
                    /* Liked Items Content */
                    <div>
                        <h2 style={commonStyles.textLightBold(20, { 
                            color: colors.black,
                            marginBottom: 16
                        })}>
                            Your Liked Items
                        </h2>
                        
                        {likedBooks.length > 0 ? (
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
                                gap: 16
                            }}>
                                {likedBooks.map((book) => {
                                    // Safely extract author name
                                    let authorName = 'Unknown Author';
                                    if (typeof book.author === 'string') {
                                        authorName = book.author;
                                    } else if (book.author && typeof book.author === 'object' && book.author.name) {
                                        authorName = book.author.name;
                                    } else if (book.author_name) {
                                        authorName = book.author_name;
                                    }
                                    
                                    // Extract audio URL if available
                                    const audioUrl = book.bookaudio ? `${FILE_BASE_URL}${book.bookaudio}` : (book.sample_audio || null);
                                    
                                    // Ensure book has required properties with proper image URLs
                                    const safeBook = {
                                        id: book.id,
                                        title: book.title || 'Untitled',
                                        author: authorName,
                                        author_name: authorName,
                                        coverimage: book.coverimage ? `${FILE_BASE_URL}${book.coverimage}` : '/logo192.png',
                                        image: book.image ? `${FILE_BASE_URL}${book.image}` : null,
                                        rating: parseFloat(book.average_rating) || 0,
                                        is_liked: true, // These are liked books
                                        audio_url: audioUrl,
                                        bookaudio: book.bookaudio,
                                        bookfile: book.bookfile ? `${FILE_BASE_URL}${book.bookfile}` : null
                                    };
                                    
                                    return <BookCard key={book.id} book={safeBook} />;
                                })}
                            </div>
                        ) : (
                            <div style={{
                                textAlign: 'center',
                                padding: 40,
                                color: colors.grey
                            }}>
                                <div style={{ fontSize: 18, marginBottom: 8 }}>No liked items yet</div>
                                <div style={{ fontSize: 14 }}>Start exploring and like some books!</div>
                                <button
                                    onClick={() => navigate('/home')}
                                    style={{
                                        marginTop: 16,
                                        padding: '10px 20px',
                                        backgroundColor: colors.appPrimary,
                                        color: colors.white,
                                        border: 'none',
                                        borderRadius: 6,
                                        cursor: 'pointer'
                                    }}
                                >
                                    Explore Books
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
            <BottomNavigation />
        </div>
            {loading && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: '80px', // Account for bottom nav
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 999
                }}>
                    <div style={{
                        width: 40,
                        height: 40,
                        border: `3px solid ${colors.lightGrey}`,
                        borderTop: `3px solid ${colors.appPrimary}`,
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                    }}></div>
                </div>
            )}
        </>
    );
};

export default ProfileScreen; 